
import React, { useState, useEffect, useRef } from 'react';
import { CalendarEvent, CaseFile, LegalPayment } from '../types';
import { syncCaseWithPortalStatus } from '../services/geminiService';
import { calculateRomanianDeadline, DEADLINE_PRESETS } from '../services/deadlineService';

interface CalendarViewProps {
  events: CalendarEvent[];
  cases: CaseFile[];
  onAddEvent: (e: CalendarEvent) => void;
  onUpdateEvent: (id: string, updates: Partial<CalendarEvent>) => void;
  onDeleteEvent: (id: string) => void;
  onAddCase: (c: Partial<CaseFile>) => void;
  onAddPayment: (p: LegalPayment) => void;
  onUpdateCase?: (id: string, updates: Partial<CaseFile>) => void;
}

const TYPE_LABELS: Record<string, { label: string, icon: string, color: string, active: string }> = {
  'hearing': { label: 'ȘEDINȚĂ', icon: '🏛️', color: 'bg-white border-slate-100 text-slate-400', active: 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100' },
  'meeting': { label: 'ÎNTÂLNIRE', icon: '🤝', color: 'bg-white border-slate-100 text-slate-400', active: 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-100' },
  'deadline': { label: 'TERMEN', icon: '📅', color: 'bg-white border-slate-100 text-slate-400', active: 'bg-emerald-600 border-emerald-600 text-white shadow-lg shadow-emerald-100' },
  'critical_deadline': { label: 'LIMITĂ CRITICĂ', icon: '🚨', color: 'bg-white border-slate-100 text-slate-400', active: 'bg-rose-600 border-rose-600 text-white shadow-lg shadow-rose-200 animate-pulse' },
  'memo': { label: 'NOTĂ', icon: '📝', color: 'bg-white border-slate-100 text-slate-400', active: 'bg-amber-500 border-amber-500 text-white shadow-lg shadow-amber-100' }
};

const CalendarView: React.FC<CalendarViewProps> = ({ events, cases, onAddEvent, onUpdateEvent, onDeleteEvent, onAddCase, onAddPayment, onUpdateCase }) => {
  const [selectedMonthDate, setSelectedMonthDate] = useState(new Date());
  const [activeDayModal, setActiveDayModal] = useState<number | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  
  const todayRef = useRef<HTMLDivElement>(null);

  const [form, setForm] = useState({ 
    title: '', type: 'hearing' as any, description: '', time: '09:00', linkedCaseId: '' 
  });

  const monthsRO = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
  const shortDaysRO = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

  const daysInMonth = new Date(selectedMonthDate.getFullYear(), selectedMonthDate.getMonth() + 1, 0).getDate();
  const calendarDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (todayRef.current) {
        todayRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [selectedMonthDate]);

  const handlePortalSync = async () => {
    if (!form.linkedCaseId) return;
    const linkedCase = cases.find(c => c.id === form.linkedCaseId);
    if (!linkedCase) return;
    setIsSyncing(true);
    try {
      const data = await syncCaseWithPortalStatus(linkedCase.caseNumber);
      setForm(prev => ({
        ...prev,
        description: `ACTUALIZARE PORTAL: ${data.resolution}\n\n${prev.description}`
      }));
    } catch (err) {
      alert("Eroare portal.");
    } finally {
      setIsSyncing(false);
    }
  };

  const applyCalculatedDeadline = (presetId: string) => {
    const preset = DEADLINE_PRESETS.find(p => p.id === presetId);
    if (!preset || !activeDayModal) return;
    const startDate = new Date(selectedMonthDate.getFullYear(), selectedMonthDate.getMonth(), activeDayModal);
    const targetDate = calculateRomanianDeadline(startDate.toISOString(), preset.duration, preset.type);
    onAddEvent({
      id: Math.random().toString(36).substr(2, 9),
      title: `LIMITĂ: ${preset.label}`,
      date: targetDate.toISOString(),
      type: 'critical_deadline',
      description: `Calculat automat (${preset.duration} ${preset.type.replace('_', ' ')})`,
    });
    setShowCalculator(false);
    setActiveDayModal(null);
  };

  const getSortedDayEvents = (day: number) => {
    return events.filter(e => {
      const d = new Date(e.date);
      return d.getDate() === day && d.getMonth() === selectedMonthDate.getMonth() && d.getFullYear() === selectedMonthDate.getFullYear();
    }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const handleSave = () => {
    if (!form.title || !activeDayModal) return;
    const [hours, minutes] = form.time.split(':').map(Number);
    const date = new Date(selectedMonthDate.getFullYear(), selectedMonthDate.getMonth(), activeDayModal, hours, minutes);
    
    if (selectedEventId) {
      onUpdateEvent(selectedEventId, { ...form, date: date.toISOString() });
    } else {
      onAddEvent({ id: Math.random().toString(36).substr(2, 9), ...form, date: date.toISOString() });
    }
    setActiveDayModal(null);
  };

  return (
    <div className="flex flex-col h-full w-full bg-white overflow-hidden">
      <div className="bg-white px-4 md:px-6 py-4 border-b border-slate-100 flex flex-col md:flex-row items-center justify-between shrink-0 gap-4">
        <h3 className="text-xl md:text-2xl font-black text-slate-900 italic uppercase text-center md:text-left">
          {monthsRO[selectedMonthDate.getMonth()]} {selectedMonthDate.getFullYear()}
        </h3>
        <div className="flex gap-2 w-full md:w-auto justify-center">
          <button onClick={() => setSelectedMonthDate(new Date(selectedMonthDate.setMonth(selectedMonthDate.getMonth() - 1)))} className="p-2 md:p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors w-12 flex items-center justify-center">←</button>
          <button onClick={() => setSelectedMonthDate(new Date(selectedMonthDate.setMonth(selectedMonthDate.getMonth() + 1)))} className="p-2 md:p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors w-12 flex items-center justify-center">→</button>
        </div>
      </div>

      <div className="grid grid-cols-7 text-center text-[8px] md:text-[10px] font-black text-slate-300 uppercase py-2 border-b">
        {shortDaysRO.map((d, i) => <div key={i}>{d}</div>)}
      </div>

      <div className="flex-grow overflow-y-auto">
        <div className="grid grid-cols-7 min-h-full">
          {calendarDays.map(day => {
            const dayEvents = getSortedDayEvents(day);
            const isToday = new Date().getDate() === day && new Date().getMonth() === selectedMonthDate.getMonth() && new Date().getFullYear() === selectedMonthDate.getFullYear();
            return (
              <div 
                key={day} 
                onClick={() => { setActiveDayModal(day); setForm({title: '', type: 'hearing', description: '', time: '09:00', linkedCaseId: ''}); }} 
                className={`min-h-[80px] md:min-h-[120px] border-b border-r p-1 md:p-2 cursor-pointer hover:bg-indigo-50/50 transition-colors relative ${
                  isToday 
                    ? 'bg-indigo-50/40 border-2 border-indigo-400 shadow-inner z-10' 
                    : 'border-slate-100'
                }`}
              >
                {isToday && <div className="absolute top-0 left-0 w-full h-1 bg-indigo-500 rounded-t-sm"></div>}
                <span className={`text-sm md:text-lg font-black inline-flex items-center justify-center ${
                  isToday 
                    ? 'bg-indigo-600 text-white w-6 h-6 md:w-8 md:h-8 rounded-full shadow-md' 
                    : 'text-slate-900'
                }`}>
                  {day}
                </span>
                <div className="mt-1 space-y-1">
                  {dayEvents.map(e => (
                    <div key={e.id} className="text-[7px] md:text-[9px] p-0.5 md:p-1 rounded bg-indigo-600 text-white truncate font-bold flex items-center gap-1">
                      <span className="shrink-0">{TYPE_LABELS[e.type]?.icon}</span>
                      <span className="truncate hidden sm:inline">{e.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {activeDayModal && (
        <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-xl z-[200] flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-lg rounded-[2rem] p-6 md:p-8 shadow-2xl animate-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-6">
               <h4 className="text-xl md:text-2xl font-black italic text-slate-900">{activeDayModal} {monthsRO[selectedMonthDate.getMonth()]}</h4>
               <button onClick={() => setActiveDayModal(null)} className="w-8 h-8 flex items-center justify-center bg-slate-50 rounded-full text-slate-400 font-bold">✕</button>
            </div>
            
            <div className="space-y-4">
              <input
                className="w-full p-4 bg-slate-50 text-slate-900 rounded-xl outline-none font-bold text-sm border-2 border-transparent focus:border-indigo-500 transition-all"
                placeholder="Titlu Eveniment"
                value={form.title}
                onChange={e => setForm({...form, title: e.target.value})}
              />
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="time"
                  aria-label="Ora Evenimentului"
                  className="w-full p-4 bg-slate-50 text-slate-900 rounded-xl text-sm font-bold border-2 border-transparent focus:border-indigo-500 transition-all"
                  value={form.time}
                  onChange={e => setForm({...form, time: e.target.value})}
                />
                <select
                  aria-label="Tip Eveniment"
                  className="w-full p-4 bg-slate-50 text-slate-900 rounded-xl text-[10px] font-black uppercase border-2 border-transparent focus:border-indigo-500 transition-all"
                  value={form.type}
                  onChange={e => setForm({...form, type: e.target.value as any})}
                >
                  {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              
              <div className="relative">
                <textarea
                  className="w-full p-4 bg-slate-50 text-slate-900 rounded-xl h-32 text-sm font-medium resize-none border-2 border-transparent focus:border-indigo-500 transition-all"
                  placeholder="Detalii..."
                  value={form.description}
                  onChange={e => setForm({...form, description: e.target.value})}
                />
                {form.type === 'hearing' && (
                   <button
                     onClick={handlePortalSync}
                     disabled={isSyncing || !form.linkedCaseId}
                     className="absolute bottom-4 right-4 bg-indigo-600 text-white px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-indigo-700 disabled:opacity-50 transition-all"
                   >
                     {isSyncing ? 'Sync...' : 'Check Portal'}
                   </button>
                )}
              </div>

              <div className="flex gap-4 pt-2">
                <button onClick={() => setActiveDayModal(null)} className="flex-1 py-4 text-slate-400 font-black text-xs uppercase tracking-widest hover:bg-slate-50 rounded-xl transition-all">Anulează</button>
                <button onClick={handleSave} className="flex-1 py-4 bg-indigo-600 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-xl shadow-indigo-200 hover:bg-indigo-700 active:scale-95 transition-all">Salvează</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;
